import { createDoorPassClient } from "@libs/ticketing-client";
import { randomUUID } from "expo-crypto";
import { captureException } from "@utils/captureException";
import type { RouterOutputs } from "@utils/trpc";

type Door = RouterOutputs["user"]["accesses"]["openDoor"];
type Claim = RouterOutputs["user"]["accesses"]["claimTicket"];
export type ScanResult = { scannedBefore: boolean; holder: string | null };

const messages: Record<string, string> = {
  position_revoked: "This ticket has been revoked or refunded.",
  wrong_event: "This ticket is for a different event.",
  code_not_found: "Ticket not found.",
  door_pass_expired:
    "The door pass has expired. Try again to obtain a new pass.",
  invalid_api_key:
    "The door pass is invalid or has been revoked. Try again to obtain a new pass.",
};

// Only known server messages are safe to display or send to telemetry.
const refusalMessages: Record<string, string> = {
  "Ticket not found for redeem code": "Ticket not found.",
  "This feature is not available in this version of the app.":
    "This feature is not available in this version of the app. Update the app to scan tickets.",
  "userId does not manage artistId":
    "You do not have manager access to scan this event's tickets.",
  "Access not found": "Access not found.",
  "Access has no headliner artist":
    "This event has no headliner artist. Contact the event organizer.",
  "A manager email is required to open the door":
    "A manager email is required to open the door.",
  "Ticket scanning is not configured":
    "Ticket scanning is not configured. Contact support.",
  "door_pass:invalid_door_pass_expiry":
    "The scanning window for this event has ended. Contact the event organizer.",
  "door_pass:invalid_api_key":
    "Ticket scanning authorization is unavailable. Contact support.",
  "door_pass:not_found":
    "The ticketing event or organizer could not be found. Contact support.",
};

/** Map only known client refusals to safe scanner copy. */
function refusalMessage(error: unknown): string | undefined {
  if (!(error instanceof Error) || !("data" in error)) return;
  const data = error.data as { httpStatus?: number } | undefined;
  if (!data?.httpStatus || data.httpStatus < 400 || data.httpStatus >= 500)
    return;
  return (
    refusalMessages[error.message] ??
    "This request cannot be completed. Contact support."
  );
}

/** One open scanner owns this credential. It never enters a query/mutation cache. */
export function createScannerSession(deps: {
  openDoor: (idempotencyKey: string) => Promise<Door>;
  claim: (code: string, idempotencyKey: string) => Promise<Claim>;
  newScanId: () => string;
  newDoorId?: () => string;
  fetch?: (request: Request) => Promise<Response>;
}) {
  let door: Door | undefined;
  let disposed = false;
  let opening: Promise<void> | undefined;
  const newDoorId = deps.newDoorId ?? randomUUID;
  let doorOpenId = newDoorId();
  const scanIds = new Map<string, string>();
  const activeRequests = new Set<AbortController>();
  const open = () => {
    if (disposed) return Promise.reject(new Error("Scanner closed."));
    if (!opening) {
      opening = deps
        .openDoor(doorOpenId)
        .then((value) => {
          if (!disposed) door = value;
        })
        .catch((error: unknown) => {
          throw new Error(
            refusalMessage(error) ??
              "Unable to open the scanner. Check your connection and manager access, then try again.",
          );
        })
        .finally(() => {
          opening = undefined;
        });
    }
    return opening;
  };
  return {
    open,
    dispose() {
      disposed = true;
      for (const request of activeRequests) request.abort();
      activeRequests.clear();
      scanIds.clear();
      door = undefined;
    },
    async scan(code: string): Promise<ScanResult> {
      if (disposed) throw new Error("Scanner closed.");
      if (!door) await open();
      if (disposed || !door) throw new Error("Scanner closed.");
      let scanId = scanIds.get(code);
      if (!scanId) {
        scanId = deps.newScanId();
        scanIds.set(code, scanId);
      }
      if (door.status === "not_engine_backed") {
        try {
          const claim = await deps.claim(code, scanId);
          scanIds.delete(code);
          return {
            scannedBefore: claim.scannedBefore,
            holder: claim.user?.name ?? claim.userEmail ?? null,
          };
        } catch (error) {
          if (!(error instanceof Error) || error.message !== "use_door_pass") {
            const sanitized = new Error(
              refusalMessage(error) ??
                "Unable to validate this ticket. Check your connection and access, then try again.",
            );
            captureException(sanitized, {
              channel: "error",
              area: "ticket-scan",
              operation: "claim-ticket",
            });
            throw sanitized;
          }
          await open();
        }
      }
      if (disposed || !door) throw new Error("Scanner closed.");
      if (door.status !== "ready")
        throw new Error(
          "This ticket requires the scanner for its engine-backed event.",
        );
      const controller = new AbortController();
      activeRequests.add(controller);
      const timeout = setTimeout(() => controller.abort(), 15_000);
      const client = createDoorPassClient({
        baseUrl: door.apiBaseUrl,
        token: door.token,
        fetch: (request) =>
          (deps.fetch ?? fetch)(
            new Request(request, { signal: controller.signal }),
          ),
      });
      let result: Awaited<ReturnType<typeof client.scan>>;
      try {
        result = await client.scan({
          code,
          client_scan_id: scanId,
          scanned_at: new Date().toISOString(),
          manual: false,
        });
      } catch {
        throw new Error(
          "Unable to check in. Check your connection and scan again.",
        );
      } finally {
        clearTimeout(timeout);
        activeRequests.delete(controller);
      }
      if (disposed) throw new Error("Scanner closed.");
      // A settled response ends replay; only failed transport retains the ID.
      scanIds.delete(code);
      if (result.data?.outcome === "admitted") {
        return {
          scannedBefore: false,
          holder: result.data.position?.buyer_name ?? null,
        };
      }
      const errorCode = result.error?.error.code;
      if (
        errorCode === "invalid_api_key" ||
        errorCode === "door_pass_expired"
      ) {
        door = undefined;
        doorOpenId = newDoorId();
      }
      if (errorCode === "already_checked_in")
        return { scannedBefore: true, holder: null };
      throw new Error(
        messages[errorCode ?? ""] ?? "Unable to check in. Please try again.",
      );
    },
  };
}
